import { IconExternalLink } from '@tabler/icons-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import config from '@/config';
import type { ApiKey, ListApiKeysQueryError, ListApiKeysQueryResult } from '@/modules/api/codegen';
import { SearchInput } from '@/modules/forms';
import { Alert, Link, SortIcon, Table, Text } from '@/modules/ui';

import { CopyIconButton } from '../CopyIconButton';
import * as felaRules from './ApiKeysTable.rules';
import { BatchActions } from './BatchActions';
import { useApiKeysTableColumns, useApiKeysTableState, type ApiKeysSortField } from './hooks';

export interface ApiKeysTableProps {
    query: UseQueryResult<ListApiKeysQueryResult, ListApiKeysQueryError>;
}

export const ApiKeysTable = ({ query }: ApiKeysTableProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();

    const { rows, total, search, setSearch, sort, setSort, page, pageSize, setPagination, selectedRowKeys, setSelectedRowKeys } =
        useApiKeysTableState(query.data?.data);

    const columns = useApiKeysTableColumns(sort);

    const isEmpty = !rows.length;

    return (
        <div className={css(felaRules.container)}>
            <div className={css(felaRules.filters)}>
                <div className={css(felaRules.filtersStart)}>
                    <Link type='native' size='regular' href={config.links.docsPublicApi} customStyle={felaRules.docsLink}>
                        <FormattedMessage id='api-keys.docs' />
                        <IconExternalLink size={16} aria-hidden className={css(felaRules.docsLinkIcon)} />
                    </Link>
                    <div className={css(felaRules.baseUrl)}>
                        <Text elementType='span' variant='ParagraphSmallMedium' color='foregroundsTertiary'>
                            <FormattedMessage id='api-keys.baseUrl.label' />
                        </Text>
                        <Text elementType='span' variant='ParagraphSmallMedium' customStyle={felaRules.baseUrlValue}>
                            {config.publicApi.baseUrl}
                        </Text>
                        <CopyIconButton value={config.publicApi.baseUrl} iconSize={16} data-testid='copy-api-base-url-button' />
                    </div>
                </div>
                {selectedRowKeys.length > 0 ? (
                    <BatchActions apiKeyIds={selectedRowKeys.map(String)} onClear={() => setSelectedRowKeys([])} />
                ) : (
                    <SearchInput
                        placeholder={formatMessage({ id: 'api-keys.filters.search.placeholder' })}
                        customStyle={felaRules.searchInput}
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                    />
                )}
            </div>
            <ConfigProvider
                renderEmpty={() => (
                    <div className={css(felaRules.stateBox)}>
                        {query.error ? (
                            <Alert
                                type='error'
                                title={<FormattedMessage id='api-keys.table.loadingError' />}
                                customStyle={felaRules.errorAlert}
                            />
                        ) : (
                            <Text>
                                <FormattedMessage id={search ? 'api-keys.table.noResults' : 'api-keys.table.empty'} />
                            </Text>
                        )}
                    </div>
                )}
            >
                <Table<ApiKey>
                    className={css(felaRules.table)}
                    tableLayout='fixed'
                    columns={columns.map(column => ({
                        ...column,
                        showSorterTooltip: false,
                        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
                    }))}
                    dataSource={rows}
                    rowKey='id'
                    showHeader={!isEmpty}
                    loading={query.isLoading}
                    rowSelection={{
                        columnWidth: 40,
                        // Rows are paged client side, so without this a selection would be dropped
                        // the moment the row leaves the current page.
                        preserveSelectedRowKeys: true,
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    }}
                    onChange={(_pagination, _filters, sorter) => {
                        if (Array.isArray(sorter)) return;

                        setSort(sorter.field as ApiKeysSortField, sorter.order ?? null);
                    }}
                    pagination={{ current: page, pageSize, total, onChange: setPagination }}
                />
            </ConfigProvider>
        </div>
    );
};
